import { MIRROR_FITNESS_CARDS_ENABLED } from '@/lib/featureFlags';
import { getFitnessWorkoutRuntime, reviewFitnessWorkoutControl, dispatchFitnessWorkoutControl, type FitnessWorkoutControlReview, type FitnessWorkoutRuntime } from '@/lib/fitnessWorkoutController';
import { fitnessLocalWorkoutId } from '../../supabase/functions/_shared/fitnessLocalWorkoutDomain';
import { canonicalFitnessRuntimeActionArgs, canonicalFitnessRuntimeEnvelope, type FitnessRuntimeAction, type FitnessRuntimeEnvelope, type FitnessRuntimeState } from '../../supabase/functions/_shared/fitnessRuntimeDomain';
import { fitnessRuntimeActionRequested } from '../../supabase/functions/_shared/fitnessRuntimeIntent';

export interface FitnessRuntimeAuth {
  getUser(): PromiseLike<{ data: { user: { id: string } | null }; error: unknown }>;
  getSession(): PromiseLike<{ data: { session: { user: { id: string }; access_token: string } | null }; error: unknown }>;
}
interface Origin { ownerId: string; auth: FitnessRuntimeAuth; isCurrent: () => boolean; now?: () => number }
export interface FitnessRuntimeRequestOptions extends Origin { text: string }
export interface FitnessRuntimeActionOptions extends Origin {
  sessionId: string; actionId: string; args: unknown; request: FitnessRuntimeEnvelope;
}
export interface PreparedFitnessRuntimeAction {
  readonly operationId: string; readonly action: FitnessRuntimeAction; readonly summary: FitnessRuntimeState;
}
export interface FitnessRuntimeActionReceipt {
  readonly version: 1; readonly owner: string; readonly sessionId: string; readonly actionId: string; readonly operationId: string;
  readonly action: FitnessRuntimeAction; readonly runtimeId: string; readonly epoch: number; readonly revision: number;
  readonly phase: FitnessRuntimeState['phase']; readonly paused: boolean; readonly observedAt: string;
  readonly status: 'succeeded'; readonly scope: 'browser_session_runtime'; readonly evidence: 'controller_readback';
}
interface IssuedRequest { ownerId: string; action: FitnessRuntimeAction; identity: FitnessWorkoutControlReview | null }
interface PreparedPrivate {
  options: FitnessRuntimeActionOptions; expected: FitnessWorkoutControlReview; preparedAt: number;
  busy: boolean; dispatched: boolean; receipt: FitnessRuntimeActionReceipt | null;
}
const issuedRequests = new WeakMap<FitnessRuntimeEnvelope, IssuedRequest>();
const preparedPrivate = new WeakMap<PreparedFitnessRuntimeAction, PreparedPrivate>();
const issuedReceipts = new WeakMap<FitnessRuntimeActionReceipt, { ownerId: string; sessionId: string; actionId: string }>();
export const FITNESS_RUNTIME_REVIEW_TTL_MS = 2 * 60 * 1000;
const fail = (code: string): never => { throw new Error(code); };
const nowOf = (options: Origin) => options.now?.() ?? Date.now();
const uuid = (value: unknown): value is string => fitnessLocalWorkoutId(value) && typeof value === 'string' && value === value.toLowerCase();
const freeze = <T,>(value: T): T => { if (value && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); } return value; };
const captureAuth = (auth: FitnessRuntimeAuth): FitnessRuntimeAuth => ({ getUser: auth.getUser.bind(auth), getSession: auth.getSession.bind(auth) });
const identityOf = (runtime: FitnessWorkoutRuntime | null): FitnessWorkoutControlReview | null => runtime ? {
  ownerId: runtime.ownerId, runId: runtime.runId, epoch: runtime.epoch, revision: runtime.revision, phase: runtime.state.phase, paused: runtime.paused,
} : null;
const sameIdentity = (a: FitnessWorkoutControlReview | null, b: FitnessWorkoutControlReview | null) => JSON.stringify(a) === JSON.stringify(b);
function assertOrigin(options: Origin): void {
  if (!uuid(options.ownerId) || !options.isCurrent()) fail('invalid_session');
  if ('sessionId' in options && (!uuid(options.sessionId) || !('actionId' in options) || !uuid(options.actionId))) fail('invalid_session');
}
async function authenticate(options: Origin): Promise<void> {
  assertOrigin(options);
  const user = await options.auth.getUser(); assertOrigin(options);
  if (user.error || user.data.user?.id !== options.ownerId) fail('invalid_session');
  const session = await options.auth.getSession(); assertOrigin(options);
  if (session.error || session.data.session?.user.id !== options.ownerId || !session.data.session.access_token) fail('invalid_session');
}
function project(runtime: FitnessWorkoutRuntime | null, ownerId: string, now: number): FitnessRuntimeEnvelope {
  const base = { version: 1 as const, owner: ownerId, observedAt: new Date(now).toISOString() };
  if (!runtime || runtime.state.phase === 'complete') return { ...base, status: 'absent', runtime: null };
  if (runtime.state.phase === 'setup' && (!runtime.paused || !runtime.state.exercises.length)) return { ...base, status: 'unavailable', runtime: null };
  const state = runtime.state;
  try {
    return canonicalFitnessRuntimeEnvelope({ ...base, status: 'available', runtime: {
      runtimeId: runtime.runId, revision: runtime.revision, paused: runtime.paused, busy: runtime.busy !== null, phase: state.phase,
      workoutName: state.workoutName || null, currentExerciseIndex: state.currentExerciseIndex, currentSet: state.currentSet,
    } }, ownerId, new Date(now));
  } catch { return { ...base, status: 'invalid', runtime: null }; }
}

/** Only a literal current own request can read the actual living controller.
 * Private epoch/run authority stays in this module, never in the HTTP body. */
export async function readFitnessRuntimeForRequest(input: FitnessRuntimeRequestOptions): Promise<FitnessRuntimeEnvelope | null> {
  const options = { ...input, auth: captureAuth(input.auth) }, action = fitnessRuntimeActionRequested(options.text);
  if (!action) return null;
  assertOrigin(options);
  const identity = identityOf(getFitnessWorkoutRuntime(options.ownerId));
  await authenticate(options);
  const runtime = getFitnessWorkoutRuntime(options.ownerId);
  if (!sameIdentity(identity, identityOf(runtime))) fail('fitness_runtime_changed');
  const envelope = freeze(project(runtime, options.ownerId, nowOf(options)));
  assertOrigin(options);
  if (!sameIdentity(identity, identityOf(getFitnessWorkoutRuntime(options.ownerId)))) fail('fitness_runtime_changed');
  issuedRequests.set(envelope, { ownerId: options.ownerId, action, identity });
  return envelope;
}

/** A structural projection cannot prove that this browser issued the request. */
export function isFitnessRuntimeRequestCurrent(request: FitnessRuntimeEnvelope): boolean {
  const issued = issuedRequests.get(request);
  return !!issued && sameIdentity(issued.identity, identityOf(getFitnessWorkoutRuntime(issued.ownerId)));
}
function requestAuthority(options: FitnessRuntimeActionOptions, action: FitnessRuntimeAction): FitnessWorkoutControlReview {
  const issued = issuedRequests.get(options.request);
  if (!issued || issued.ownerId !== options.ownerId || issued.action !== action || !issued.identity) fail('review_required');
  if (!isFitnessRuntimeRequestCurrent(options.request)) fail('fitness_runtime_changed');
  const review = reviewFitnessWorkoutControl(options.ownerId, action);
  if (!sameIdentity(issued.identity, review)) fail('fitness_runtime_changed');
  if (!Number.isSafeInteger(review.revision) || review.revision >= Number.MAX_SAFE_INTEGER) fail('fitness_runtime_changed');
  return review;
}

/** Preparing never starts/restores a workout or changes controller state. */
export async function prepareFitnessRuntimeAction(input: FitnessRuntimeActionOptions): Promise<PreparedFitnessRuntimeAction> {
  // Capture caller-controlled primitives, methods and payload before any await.
  const options = { ...input, args: structuredClone(input.args), auth: captureAuth(input.auth) };
  assertOrigin(options);
  if (!MIRROR_FITNESS_CARDS_ENABLED) fail('fitness_runtime_disabled');
  const args = canonicalFitnessRuntimeActionArgs(options.args, options.ownerId, new Date(nowOf(options)));
  if (JSON.stringify(args.snapshot) !== JSON.stringify(options.request)) fail('snapshot_changed');
  const expected = requestAuthority(options, args.action);
  await authenticate(options);
  if (!sameIdentity(expected, requestAuthority(options, args.action))) fail('fitness_runtime_changed');
  await authenticate(options);
  if (!sameIdentity(expected, requestAuthority(options, args.action))) fail('fitness_runtime_changed');
  const operationId = crypto.randomUUID();
  if (!uuid(operationId)) fail('invalid_operation');
  const prepared = freeze({ operationId, action: args.action, summary: structuredClone(args.snapshot.runtime!) });
  preparedPrivate.set(prepared, { options, expected, preparedAt: nowOf(options), busy: false, dispatched: false, receipt: null });
  return prepared;
}
function assertAge(state: PreparedPrivate): void {
  const age = nowOf(state.options) - state.preparedAt;
  if (!Number.isFinite(age) || age < 0 || age > FITNESS_RUNTIME_REVIEW_TTL_MS) fail('review_expired');
}
export function fitnessRuntimeActionReceiptMatches(receipt: FitnessRuntimeActionReceipt, ownerId: string, sessionId: string, actionId?: string): boolean {
  const issued = issuedReceipts.get(receipt);
  return !!issued && issued.ownerId === ownerId && issued.sessionId === sessionId && (actionId === undefined || issued.actionId === actionId);
}

/** Final auth, exact private CAS and independent readback. Unknown outcomes are
 * terminal for this prepared operation; acknowledgement retries never dispatch. */
export async function confirmFitnessRuntimeAction(prepared: PreparedFitnessRuntimeAction): Promise<FitnessRuntimeActionReceipt> {
  const state = preparedPrivate.get(prepared);
  if (!state) return fail('review_required');
  if (state.busy) return fail('fitness_runtime_busy');
  state.busy = true;
  try {
    const { options, expected } = state;
    await authenticate(options);
    if (state.receipt) return state.receipt;
    if (state.dispatched) fail('outcome_unknown');
    if (!MIRROR_FITNESS_CARDS_ENABLED) fail('fitness_runtime_disabled');
    assertAge(state);
    if (!sameIdentity(expected, requestAuthority(options, prepared.action))) fail('fitness_runtime_changed');
    await authenticate(options);
    assertAge(state); assertOrigin(options);
    if (!MIRROR_FITNESS_CARDS_ENABLED) fail('fitness_runtime_disabled');
    if (!sameIdentity(expected, requestAuthority(options, prepared.action))) fail('fitness_runtime_changed');
    // No await from final compare through dispatch and independent readback.
    // If dispatch throws after touching state, this operation is never replayed.
    state.dispatched = true;
    try {
      const result = dispatchFitnessWorkoutControl(expected, prepared.action);
      const observedAt = new Date(nowOf(options)).toISOString();
      // Authority callbacks may revoke or mutate state synchronously. Run them
      // before the final independent readback, then issue plain frozen data.
      assertOrigin(options);
      const actual = getFitnessWorkoutRuntime(options.ownerId), identity = identityOf(actual);
      const intended = { ...expected, revision: expected.revision + 1, paused: prepared.action === 'pause' };
      if (!identity || !sameIdentity(identity, intended) || result.scope !== 'browser_session_runtime' || result.evidence !== 'controller_readback' || result.action !== prepared.action
        || !sameIdentity({ ownerId: result.ownerId, runId: result.runId, epoch: result.epoch, revision: result.revision, phase: result.phase, paused: result.paused }, intended) || actual!.busy) fail('outcome_unknown');
      const receipt: FitnessRuntimeActionReceipt = freeze({
        version: 1, owner: options.ownerId, sessionId: options.sessionId, actionId: options.actionId, operationId: prepared.operationId,
        action: prepared.action, runtimeId: identity.runId, epoch: identity.epoch, revision: identity.revision, phase: identity.phase as FitnessRuntimeState['phase'],
        paused: identity.paused, observedAt, status: 'succeeded', scope: 'browser_session_runtime', evidence: 'controller_readback',
      });
      issuedReceipts.set(receipt, { ownerId: options.ownerId, sessionId: options.sessionId, actionId: options.actionId });
      state.receipt = receipt; return receipt;
    } catch { return fail('outcome_unknown'); }
  } finally { state.busy = false; }
}
